import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  BadgeAlert,
  ChevronRight,
  Clock3,
  Copy,
  ExternalLink,
  Globe2,
  LoaderCircle,
  Newspaper,
  RefreshCcw,
  Rss,
  Sparkles,
  Youtube,
} from 'lucide-react';
import { gsap } from 'gsap';
import { Button } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { SkeletonLine } from '@auw/workspace-ui';
import {
  getFeedHistory,
  getLatestFeedSnapshot,
  refreshFeedSnapshot,
  type FeedCard,
  type FeedDailySnapshot,
  type FeedLeadCard,
  type FeedLeadCardSource,
  type FeedLane,
  type FeedLatestResponse,
  type FeedSourceType,
  type FeedStatusResponse,
} from '@auw/api-client';
import { workspaceSSE } from '@auw/api-client';
import { emitAppEvent } from '@auw/stores';
import { useUIStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useFeature } from '@auw/feature-flags';
import { ADMIN_FEED_REFRESH, usePermission } from '@auw/permissions';
import { useTranslation } from 'react-i18next';
import { getInterestDisplayLabel } from '@auw/i18n';

const FEED_PAGE_MAX_WIDTH = 1280;
const FEED_LANE_CARD_WIDTH = 360;
const FEED_LANE_CARD_HEIGHT = 392;
const FEED_ITEM_THUMBNAIL_HEIGHT = 100;

const FEED_HISTORY_FALLBACK_LIMIT = 6;

function formatTodayDate(): string {
  return new Date().toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
  });
}

function formatAbsoluteDate(value?: string | null): string {
  if (!value) return formatTodayDate();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return formatTodayDate();
  return parsed.toLocaleString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function splitLeadDetailParagraphs(value: string): string[] {
  const normalized = value.replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  const blockParagraphs = normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (blockParagraphs.length > 1) return blockParagraphs;

  return normalized
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function parseLeadParagraph(value: string): { label: string | null; body: string } {
  const normalized = value.trim();
  const labeledMatch = normalized.match(/^([^:\n]{2,32}):\s*(.+)$/s);
  if (!labeledMatch) {
    return { label: null, body: normalized };
  }

  return {
    label: labeledMatch[1].trim(),
    body: labeledMatch[2].trim(),
  };
}

function hasDistinctOriginalTitle(card: FeedCard): boolean {
  const current = card.title.replace(/\s+/g, ' ').trim().toLowerCase();
  const original = (card.original_title ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  return Boolean(current && original && current !== original);
}

const SOURCE_TYPE_KEY: Record<string, string> = {
  youtube: 'feed.youtube',
  blog: 'feed.blog',
  news: 'feed.news',
  discover: 'feed.webSearch',
};

function buildSourceLabelKey(sourceType: FeedSourceType): string {
  return SOURCE_TYPE_KEY[sourceType] ?? 'feed.webSearch';
}

function getSourceIcon(sourceType: FeedSourceType): typeof Youtube {
  switch (sourceType) {
    case 'youtube':
      return Youtube;
    case 'blog':
      return Rss;
    case 'news':
      return Newspaper;
    default:
      return Globe2;
  }
}

function getInterestLabel(value: string, customInterests: string[] = []): string {
  return getInterestDisplayLabel(value, customInterests);
}

function buildLeadCardSources(items: FeedCard[]): FeedLeadCardSource[] {
  const seen = new Set<string>();
  return items
    .filter((item) => {
      if (!item.source_url || seen.has(item.source_url)) return false;
      seen.add(item.source_url);
      return true;
    })
    .slice(0, 4)
    .map((item) => ({
      title: item.title,
      source_name: item.source_name,
      source_url: item.source_url,
      source_type: item.source_type,
    }));
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function buildFallbackLeadCard(lane: FeedLane, t: TFn): FeedLeadCard | null {
  if (lane.items.length === 0) return null;
  const topItems = lane.items.slice(0, 4);
  const snippet = [topItems[0]?.summary, topItems[1]?.summary]
    .filter(Boolean)
    .join(' ')
    .trim();
  const detailBody = [
    t('feed.laneIntroduction', { title: lane.title }),
    ...topItems.slice(0, 3).map((item) => `${item.title}: ${item.summary}`),
    t('feed.detailCardAdvice'),
  ].join('\n\n');

  return {
    card_id: `lead-${lane.lane_id}`,
    title: t('feed.atAGlanceTitle', { title: lane.title }),
    snippet: snippet || t('feed.fallbackSnippet'),
    detail_body: detailBody,
    key_points: topItems.map((item) => item.title).slice(0, 4),
    source_links: buildLeadCardSources(topItems),
    interest_key: lane.interest_key,
    related_dedupe_keys: topItems.map((item) => item.dedupe_key).slice(0, 4),
  };
}

function withFallbackLeadCard(lane: FeedLane, t: TFn): FeedLane {
  if (lane.lead_card) return lane;
  return {
    ...lane,
    lead_card: buildFallbackLeadCard(lane, t),
  };
}

function deriveLanes(
  snapshot: FeedDailySnapshot | null,
  interests: string[],
  customInterests: string[],
  t: TFn,
): FeedLane[] {
  if (snapshot?.lanes?.length) return snapshot.lanes.map((lane) => withFallbackLeadCard(lane, t));

  const fallbackCards = snapshot?.hero_cards ?? [];
  if (fallbackCards.length === 0) {
    return interests.map((interest, index) => ({
      lane_id: `fallback-${interest}-${index}`,
      title: t('feed.interestInsight', { interest: getInterestLabel(interest, customInterests) }),
      interest_key: interest,
      description: t('feed.preparingUpdate'),
      lead_card: null,
      items: [],
    }));
  }

  return interests.map((interest, index) => withFallbackLeadCard({
    lane_id: `derived-${interest}-${index}`,
    title: t('feed.interestInsight', { interest: getInterestLabel(interest, customInterests) }),
    interest_key: interest,
    description: t('feed.todayHighlights'),
    lead_card: null,
    items: fallbackCards.filter((card) => card.interest_key === interest || index === 0),
  }, t));
}

function buildHeroCards(snapshot: FeedDailySnapshot | null, lanes: FeedLane[]): FeedCard[] {
  const preferred = snapshot?.hero_cards ?? [];
  if (preferred.length >= 2) return preferred.slice(0, 2);
  const fromLanes = lanes.flatMap((lane) => lane.items);
  return [...preferred, ...fromLanes].slice(0, 2);
}

function resolveDetailLane(card: FeedCard, lanes: FeedLane[], customInterests: string[], t: TFn): FeedLane {
  const matchedLane = lanes.find((lane) =>
    lane.items.some((item) =>
      item.card_id === card.card_id
      || item.dedupe_key === card.dedupe_key
      || Boolean(card.story_id && item.story_id && card.story_id === item.story_id),
    ));

  if (matchedLane) return matchedLane;

  const interestKey = card.interest_key || 'discover';
  return {
    lane_id: `hero-${card.card_id}`,
    title: t('feed.cardDetail', { title: getInterestLabel(interestKey, customInterests) }),
    interest_key: interestKey,
    description: null,
    lead_card: null,
    items: [card],
  };
}

function buildLeadBriefCopyText(leadCard: FeedLeadCard, sourceLabel: string): string {
  const detailBody = leadCard.detail_body.trim() || leadCard.snippet.trim();
  const sourceText = leadCard.source_links
    .map((source) => {
      const label = source.title || source.source_name;
      const sourceMeta = source.source_name && source.source_name !== label
        ? `${label} | ${source.source_name}`
        : label;
      return `- ${sourceMeta}\n  ${source.source_url}`;
    })
    .join('\n');

  return [
    leadCard.title.trim(),
    detailBody,
    sourceText ? `${sourceLabel}\n${sourceText}` : '',
  ].filter(Boolean).join('\n\n');
}

function laneHasRenderableContent(lane: FeedLane): boolean {
  return Boolean(lane.lead_card) || lane.items.length > 0;
}

function snapshotHasRenderableContent(snapshot: FeedDailySnapshot | null | undefined): boolean {
  if (!snapshot) return false;
  if (snapshot.hero_cards.length > 0) return true;
  return snapshot.lanes.some(laneHasRenderableContent);
}

type FeedDetailModalState =
  | {
      kind: 'lead';
      lane: FeedLane;
      leadCard: FeedLeadCard;
    }
  | {
      kind: 'card';
      lane: FeedLane;
      card: FeedCard;
    };

interface FeedLaneCarouselProps {
  lane: FeedLane;
  direction: 'left' | 'right';
  onOpenCard: (lane: FeedLane, item: FeedCard) => void;
}

const CARD_SCROLL_GAP = 16;
const ARROW_PAUSE_MS = 4000;

const FeedLaneCarousel: React.FC<FeedLaneCarouselProps> = ({ lane, direction, onOpenCard }) => {
  const { t } = useTranslation('common');
  const trackRef = useRef<HTMLDivElement>(null);
  const baseGroupRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hovered, setHovered] = useState(false);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const baseGroup = baseGroupRef.current;
    if (!track || !baseGroup || lane.items.length === 0) return undefined;

    const distance = Math.max(baseGroup.scrollWidth + CARD_SCROLL_GAP, 1);
    const fromX = direction === 'left' ? 0 : -distance;
    const toX = direction === 'left' ? -distance : 0;

    tweenRef.current?.kill();
    gsap.set(track, { x: fromX });
    tweenRef.current = gsap.to(track, {
      x: toX,
      duration: Math.max(26, distance / 28),
      ease: 'none',
      repeat: -1,
    });

    return () => {
      tweenRef.current?.kill();
      tweenRef.current = null;
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [direction, lane.items]);

  const nudge = useCallback(() => {
    const tween = tweenRef.current;
    const track = trackRef.current;
    const baseGroup = baseGroupRef.current;
    if (!tween || !track || !baseGroup) return;

    tween.pause();
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);

    const step = FEED_LANE_CARD_WIDTH + CARD_SCROLL_GAP;
    const currentX = gsap.getProperty(track, 'x') as number;
    const nudgeX = -step;
    const targetX = currentX + nudgeX;

    gsap.to(track, {
      x: targetX,
      duration: 0.5,
      ease: 'power2.inOut',
      onComplete: () => {
        const rawX = gsap.getProperty(track, 'x') as number;
        const totalDist = baseGroup.scrollWidth + CARD_SCROLL_GAP;
        const startX = direction === 'left' ? 0 : -totalDist;
        const endX = direction === 'left' ? -totalDist : 0;
        const range = endX - startX;
        const wrapped = range !== 0
          ? startX + ((((rawX - startX) % range) + range) % range)
          : startX;

        gsap.set(track, { x: wrapped });
        tween.progress(range !== 0 ? (wrapped - startX) / range : 0);

        pauseTimerRef.current = setTimeout(() => {
          if (!hovered) tween.resume();
        }, ARROW_PAUSE_MS);
      },
    });
  }, [direction, hovered]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    tweenRef.current?.pause();
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (!pauseTimerRef.current) tweenRef.current?.resume();
  }, []);

  if (lane.items.length === 0) {
    return (
      <LaneEmptyCard>
        <LaneEmptyIcon>
          <Sparkles size={16} />
        </LaneEmptyIcon>
        <LaneEmptyTitle>{t('feed.preparingNextCuration')}</LaneEmptyTitle>
        <LaneEmptyDesc>{t('feed.interestSignalNotice')}</LaneEmptyDesc>
      </LaneEmptyCard>
    );
  }

  return (
    <LaneCarouselWrap
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LaneViewport>
        <LaneTrack ref={trackRef}>
          <LaneGroup ref={baseGroupRef}>
            {lane.items.map((item) => (
              <FeedLaneCard
                key={`${lane.lane_id}-${item.card_id}`}
                item={item}
                onOpenDetails={() => onOpenCard(lane, item)}
              />
            ))}
          </LaneGroup>
          <LaneGroup aria-hidden="true">
            {lane.items.map((item) => (
              <FeedLaneCard
                key={`${lane.lane_id}-${item.card_id}-clone`}
                item={item}
                onOpenDetails={() => onOpenCard(lane, item)}
              />
            ))}
          </LaneGroup>
        </LaneTrack>
      </LaneViewport>
      <LaneArrowButton $side="right" type="button" onClick={nudge} aria-label={t('feed.nextCard')}>
        <ChevronRight size={20} />
      </LaneArrowButton>
    </LaneCarouselWrap>
  );
};

const FeedLaneLeadCard: React.FC<{
  lane: FeedLane;
  leadCard: FeedLeadCard;
  customInterests: string[];
  onOpen: () => void;
}> = ({ lane, leadCard, customInterests, onOpen }) => {
  const { t } = useTranslation('common');
  return (
  <LaneLeadCard>
    <LaneLeadEyebrow>
      <Sparkles size={13} />
      {getInterestLabel(lane.interest_key, customInterests)} Brief
    </LaneLeadEyebrow>
    <LaneLeadBody>
      <LaneLeadTitle>{leadCard.title}</LaneLeadTitle>
      <LaneLeadSnippet>{leadCard.snippet}</LaneLeadSnippet>
      {leadCard.key_points.length > 0 && (
        <LaneLeadPoints>
          {leadCard.key_points.slice(0, 2).map((point) => (
            <LaneLeadPoint key={point}>{point}</LaneLeadPoint>
          ))}
        </LaneLeadPoints>
      )}
    </LaneLeadBody>
    <LaneLeadMeta>
      <span>{t('feed.sources', { count: leadCard.source_links.length })}</span>
      <span>{t('feed.cards', { count: lane.items.length })}</span>
    </LaneLeadMeta>
    <LaneLeadAction type="button" onClick={onOpen}>
      {t('feed.viewFullBrief')}
    </LaneLeadAction>
  </LaneLeadCard>
  );
};

const FeedLaneCard: React.FC<{ item: FeedCard; onOpenDetails: () => void }> = ({ item, onOpenDetails }) => {
  const { t } = useTranslation('common');
  const SourceIcon = getSourceIcon(item.source_type);
  /* P11-5i: tenants without `mode.feed.externalLinks` hide the
   * "view original" affordance on lane cards (the read-more / detail
   * modal still shows the curated summary). */
  const externalLinksEnabled = useFeature('mode.feed.externalLinks');
  return (
    <LaneCard>
      <LaneCardTopBar>
        <LaneCardPill>
          <SourceIcon size={12} />
          {t(buildSourceLabelKey(item.source_type))}
        </LaneCardPill>
      </LaneCardTopBar>
      <LaneCardBody>
        <LaneCardTitle>{item.title}</LaneCardTitle>
        <LaneCardSummary>{item.summary}</LaneCardSummary>
        <LaneCardMeta>
          <span>{item.source_name}</span>
          <span>{formatAbsoluteDate(item.published_at)}</span>
        </LaneCardMeta>
      </LaneCardBody>
      <LaneCardFooter>
        <LaneCardReason>{item.why_it_matters}</LaneCardReason>
        <LaneCardActions>
          <LaneCardActionButton type="button" onClick={onOpenDetails}>
            {t('feed.readMore')}
          </LaneCardActionButton>
          {externalLinksEnabled && (
            <LaneCardActionLink href={item.source_url} target="_blank" rel="noreferrer">
              {t('feed.viewOriginal')}
              <ExternalLink size={13} />
            </LaneCardActionLink>
          )}
        </LaneCardActions>
      </LaneCardFooter>
    </LaneCard>
  );
};

const FeedDetailModal: React.FC<{
  detail: FeedDetailModalState | null;
  customInterests: string[];
  onClose: () => void;
}> = ({ detail, customInterests, onClose }) => {
  const { t } = useTranslation('common');
  /* P11-5i: gate "view original" / "read more sources" anchors. The
   * curated body, summary, key-points and lane carousel still render. */
  const externalLinksEnabled = useFeature('mode.feed.externalLinks');
  if (!detail) return null;

  const isLeadDetail = detail.kind === 'lead';
  const modalTitle = isLeadDetail ? detail.lane.title : detail.card.title;
  const modalSubtitle = isLeadDetail
    ? `${getInterestLabel(detail.lane.interest_key, customInterests)} Brief`
    : t('feed.cardDetail', { title: detail.lane.title });
  const leadParagraphs = isLeadDetail
    ? splitLeadDetailParagraphs(detail.leadCard.detail_body).map(parseLeadParagraph)
    : [];
  const leadPublishedAt = isLeadDetail
    ? detail.lane.items.find((item) => item.published_at)?.published_at ?? null
    : null;
  const originalCardTitle = !isLeadDetail && hasDistinctOriginalTitle(detail.card)
    ? detail.card.original_title?.trim() ?? null
    : null;

  const handleCopyLeadBrief = async () => {
    if (!isLeadDetail) return;
    try {
      await navigator.clipboard.writeText(buildLeadBriefCopyText(detail.leadCard, t('feed.source')));
      useUIStore.getState().addToast({ type: 'success', title: t('feed.briefCopied') });
    } catch {
      useUIStore.getState().addToast({ type: 'error', title: t('feed.clipboardFail') });
    }
  };

  return (
    <Modal
      open={detail !== null}
      onClose={onClose}
      title={modalTitle}
      width={isLeadDetail ? '980px' : '760px'}
      defaultHeight={isLeadDetail ? 760 : 640}
      resizable
      draggable
      bodyPadding={isLeadDetail ? '0 28px 32px' : '0 24px 24px'}
    >
      <DetailModalBody>
        {detail.kind === 'lead' ? (
          <LeadDetailInner>
            <LeadDetailHeader>
              <LeadDetailHeaderRow>
                <LeadDetailKicker>{modalSubtitle}</LeadDetailKicker>
                <BriefCopyButton
                  type="button"
                  onClick={() => { void handleCopyLeadBrief(); }}
                  aria-label={t('feed.copyBrief')}
                  title={t('feed.copyBrief')}
                >
                  <Copy size={14} />
                </BriefCopyButton>
              </LeadDetailHeaderRow>
              <LeadDetailMeta>
                {leadPublishedAt && <LeadDetailMetaPill>{formatAbsoluteDate(leadPublishedAt)}</LeadDetailMetaPill>}
                <LeadDetailMetaPill>{t('feed.sources', { count: detail.leadCard.source_links.length })}</LeadDetailMetaPill>
                <LeadDetailMetaPill>{t('feed.cards', { count: detail.lane.items.length })}</LeadDetailMetaPill>
              </LeadDetailMeta>
            </LeadDetailHeader>
            <LeadDetailTitle>{detail.leadCard.title}</LeadDetailTitle>
            <DetailModalLeadSnippet>{detail.leadCard.snippet}</DetailModalLeadSnippet>
            {detail.leadCard.key_points.length > 0 && (
              <LeadDetailHighlights>
                <DetailSectionTitle>TL;DR</DetailSectionTitle>
                <DetailBulletList>
                  {detail.leadCard.key_points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </DetailBulletList>
              </LeadDetailHighlights>
            )}
            {leadParagraphs.length > 0 && (
              <LeadDetailArticle>
                {leadParagraphs.map((paragraph, index) => (
                  <LeadDetailParagraph key={`${paragraph.label ?? 'body'}-${index}`}>
                    {paragraph.label ? <LeadDetailParagraphLead>{paragraph.label}: </LeadDetailParagraphLead> : null}
                    {paragraph.body}
                  </LeadDetailParagraph>
                ))}
              </LeadDetailArticle>
            )}
            {externalLinksEnabled && detail.leadCard.source_links.length > 0 && (
              <DetailSection>
                <DetailSectionTitle>{t('feed.readMoreSources')}</DetailSectionTitle>
                <DetailSourceList>
                  {detail.leadCard.source_links.map((source) => (
                    <DetailSourceItem key={`${source.source_url}-${source.title}`}>
                      <DetailSourceLabel>
                        {t(buildSourceLabelKey(source.source_type))}
                      </DetailSourceLabel>
                      <DetailSourceAnchor href={source.source_url} target="_blank" rel="noreferrer">
                        <span>{source.title || source.source_name}</span>
                        <ExternalLink size={13} />
                      </DetailSourceAnchor>
                      <DetailSourceMeta>{source.source_name}</DetailSourceMeta>
                    </DetailSourceItem>
                  ))}
                </DetailSourceList>
              </DetailSection>
            )}
          </LeadDetailInner>
        ) : (
          <>
            <DetailModalSubtitle>{modalSubtitle}</DetailModalSubtitle>
            <DetailCardMeta>
              <span>{detail.card.source_name}</span>
              <span>{formatAbsoluteDate(detail.card.published_at)}</span>
            </DetailCardMeta>
            {originalCardTitle && (
              <DetailOriginalTitleBlock>
                <DetailOriginalTitleLabel>{t('feed.originalTitle')}</DetailOriginalTitleLabel>
                <DetailOriginalTitleText>{originalCardTitle}</DetailOriginalTitleText>
              </DetailOriginalTitleBlock>
            )}
            <DetailSection>
              <DetailSectionTitle>{t('feed.summary')}</DetailSectionTitle>
              <DetailBodyText>{detail.card.summary}</DetailBodyText>
            </DetailSection>
            {externalLinksEnabled && (
              <DetailActions>
                <DetailSourceAnchor href={detail.card.source_url} target="_blank" rel="noreferrer">
                  <span>{t('feed.viewOriginal')}</span>
                  <ExternalLink size={13} />
                </DetailSourceAnchor>
              </DetailActions>
            )}
          </>
        )}
      </DetailModalBody>
    </Modal>
  );
};

const FeedSkeleton: React.FC = () => {
  const { t } = useTranslation('common');
  return (
  <PageWrap>
    <Stage>
      <ContentArea>
        <StatusPanel $generating>
          <LoaderCircle size={16} />
          {t('feed.generatingFeed')}
        </StatusPanel>
        <HeaderRow>
          <TitleBlock>
            <HeaderStamp>
              <SkeletonLine width="84px" height="12px" />
              <SkeletonLine width="96px" height="12px" />
            </HeaderStamp>
          </TitleBlock>
        </HeaderRow>
        <HeroGrid>
          {[0, 1].map((idx) => (
            <HeroCardShell key={idx}>
              <HeroCardSurface>
                <SkeletonLine width="28%" height="12px" mb="14px" />
                <SkeletonLine width="86%" height="28px" mb="10px" />
                <SkeletonLine width="74%" height="16px" mb="8px" />
                <SkeletonLine width="68%" height="16px" mb="22px" />
                <SkeletonLine width="52%" height="12px" />
              </HeroCardSurface>
            </HeroCardShell>
          ))}
        </HeroGrid>
        {[0, 1, 2].map((idx) => (
          <LaneSection key={idx}>
            <LaneHeader>
              <LaneHeadingBlock>
                <SkeletonLine width="132px" height="18px" mb="8px" />
                <SkeletonLine width="220px" height="12px" />
              </LaneHeadingBlock>
            </LaneHeader>
            <LaneViewport>
              <LaneTrack>
                <LaneGroup>
                  {[0, 1, 2].map((cardIdx) => (
                    <LaneSkeletonCard key={cardIdx}>
                      <SkeletonLine width="72px" height="28px" radius="999px" mb="16px" />
                      <SkeletonLine width="82%" height="16px" mb="10px" />
                      <SkeletonLine width="94%" height="12px" mb="8px" />
                      <SkeletonLine width="78%" height="12px" />
                    </LaneSkeletonCard>
                  ))}
                </LaneGroup>
              </LaneTrack>
            </LaneViewport>
          </LaneSection>
        ))}
      </ContentArea>
    </Stage>
  </PageWrap>
  );
};

export const FeedPage: React.FC = () => {
  const { t } = useTranslation('common');
  const user = useUserStore((s) => s.user);
  const interests = user?.interests ?? [];
  const customInterests = user?.customInterests ?? [];
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<FeedLatestResponse | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<FeedDetailModalState | null>(null);

  const loadFeed = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (silent) {
      setFetching(true);
    } else {
      setLoading(true);
    }

    try {
      const next = await getLatestFeedSnapshot();
      if (!snapshotHasRenderableContent(next.snapshot)) {
        try {
          const history = await getFeedHistory({ limit: FEED_HISTORY_FALLBACK_LIMIT });
          const fallbackSnapshot = history.items.find((item) => snapshotHasRenderableContent(item));
          if (fallbackSnapshot) {
            setPayload({
              ...next,
              snapshot: fallbackSnapshot,
              is_fallback: true,
            });
          } else {
            setPayload(next);
          }
        } catch {
          setPayload(next);
        }
      } else {
        setPayload(next);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feed.loadFeedFail'));
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    void loadFeed();
  }, [loadFeed]);

  const prevInterestsRef = useRef<string[]>([]);
  useEffect(() => {
    const hadNone = prevInterestsRef.current.length === 0;
    const hasSome = interests.length > 0;
    prevInterestsRef.current = interests;
    if (hadNone && hasSome) {
      void loadFeed();
    }
  }, [interests, loadFeed]);

  // Supplementary SSE subscription for real-time feed refresh. Only
  // attaches when `mode.feed` is on for the active tenant — on-prem
  // builds with the feed module stripped out never reach FeedPage
  // through the UI, but a stale deep-link or QA harness could, and
  // we don't want the workspace SSE client to fan out payloads that
  // nothing else consumes.
  const feedModeEnabled = useFeature('mode.feed');
  /* P11-5i: tenants without `mode.feed.autoRefresh` opt out of the
   * realtime SSE refresh — they still see whatever was loaded at mount
   * but never get follow-up patches. */
  const autoRefreshEnabled = useFeature('mode.feed.autoRefresh');
  /* P11-5i: hero/lane card external "view original" anchors are gated
   * by `mode.feed.externalLinks`. The hero surface stays clickable but
   * routes through the detail modal instead. */
  const externalLinksEnabled = useFeature('mode.feed.externalLinks');
  useEffect(() => {
    if (!feedModeEnabled || !autoRefreshEnabled) return;
    return workspaceSSE.onEvent((evt) => {
      if (evt.type !== 'feed_updated' && evt.type !== 'scheduled_task_updated') return;
      const detail = evt.data && typeof evt.data === 'object'
        ? evt.data as Record<string, unknown>
        : {};
      const taskKey = typeof detail.task_key === 'string' ? detail.task_key : '';
      if (evt.type === 'feed_updated' || taskKey === 'daily_feed') {
        void loadFeed({ silent: true });
      }
    });
  }, [feedModeEnabled, autoRefreshEnabled, loadFeed]);

  const status: FeedStatusResponse | null = payload?.status ?? null;
  const hasInterests = interests.length > 0 || (status?.has_interests ?? false);
  const lanes = useMemo(
    () => deriveLanes(
      payload?.snapshot ?? null,
      (status?.interests ?? interests).slice(0, 3),
      customInterests,
      t,
    ),
    [customInterests, interests, payload?.snapshot, status?.interests, t],
  );
  const heroCards = useMemo(() => buildHeroCards(payload?.snapshot ?? null, lanes), [lanes, payload?.snapshot]);
  const hasRenderableCards = heroCards.length > 0 || lanes.some(laneHasRenderableContent);
  const isGenerating = status?.state === 'generating' || status?.is_generating === true;
  const isFallbackSnapshot = status?.is_fallback === true || payload?.is_fallback === true;
  const canManuallyRefresh = usePermission(ADMIN_FEED_REFRESH);
  const canShowRegenerateButton = canManuallyRefresh || import.meta.env.DEV;
  const showSkeleton = loading || (isGenerating && !payload?.snapshot);
  const headerDateLabel = payload?.snapshot?.local_date ?? status?.latest_snapshot_local_date ?? t('feed.today');

  const handleOpenInterests = useCallback(() => {
    emitAppEvent('app:openSettings', {
      initialTab: 'profile',
      focusSection: 'interests',
    });
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setFetching(true);
      const nextStatus = await refreshFeedSnapshot();
      setPayload((prev) => prev ? { ...prev, status: nextStatus } : { status: nextStatus, snapshot: null, is_fallback: false });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feed.requestNewFeedFail'));
    } finally {
      setFetching(false);
    }
  }, []);

  const handleRetryAction = useCallback(async () => {
    if (canShowRegenerateButton) {
      await handleRefresh();
      return;
    }
    await loadFeed();
  }, [canShowRegenerateButton, handleRefresh, loadFeed]);

  const handleOpenLeadCard = useCallback((lane: FeedLane) => {
    if (!lane.lead_card) return;
    setSelectedDetail({
      kind: 'lead',
      lane,
      leadCard: lane.lead_card,
    });
  }, []);

  const handleOpenLaneCard = useCallback((lane: FeedLane, card: FeedCard) => {
    setSelectedDetail({
      kind: 'card',
      lane,
      card,
    });
  }, []);

  const handleOpenHeroCard = useCallback((card: FeedCard) => {
    setSelectedDetail({
      kind: 'card',
      lane: resolveDetailLane(card, lanes, customInterests, t),
      card,
    });
  }, [customInterests, lanes]);

  if (!hasInterests) {
    return (
      <PageWrap>
        <EmptyState
          icon={<Globe2 size={42} />}
          title={t('feed.goToInterests')}
          description={t('feed.interestDescription')}
          action={(
            <Button variant="primary" size="sm" onClick={handleOpenInterests}>
              {t('feed.setInterestsInProfile')}
            </Button>
          )}
        />
      </PageWrap>
    );
  }

  if (showSkeleton) {
    return <FeedSkeleton />;
  }

  if (error && !payload?.snapshot) {
    return (
      <PageWrap>
        <EmptyState
          icon={<BadgeAlert size={42} />}
          title={t('feed.feedNotReady')}
          description={error}
          action={(
            <Button variant="secondary" size="sm" onClick={() => void loadFeed()}>
              {t('feed.reloadFeed')}
            </Button>
          )}
        />
      </PageWrap>
    );
  }

  if (status?.state === 'failed' && !payload?.snapshot) {
    return (
      <PageWrap>
        <EmptyState
          icon={<BadgeAlert size={42} />}
          title={t('feed.feedNotComplete')}
          description={status.display_message || t('feed.feedDelayed')}
          action={(
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleRetryAction()}
              disabled={fetching}
            >
              {canShowRegenerateButton
                ? (fetching ? t('feed.requesting') : t('feed.regenerate'))
                : (fetching ? t('feed.checking') : t('feed.recheckStatus'))}
            </Button>
          )}
        />
      </PageWrap>
    );
  }

  if (!hasRenderableCards) {
    return (
      <PageWrap>
        <EmptyState
          icon={<Sparkles size={42} />}
          title={t('feed.noFeedCards')}
          description={t('feed.noFeedCardsDescription')}
          action={(
            <Button variant="secondary" size="sm" onClick={() => void handleRetryAction()} disabled={fetching}>
              {canShowRegenerateButton
                ? (fetching ? t('feed.requesting') : t('feed.regenerate'))
                : (fetching ? t('feed.loading') : t('feed.reloadFeed'))}
            </Button>
          )}
        />
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <Stage>
        <ContentArea>
          {isFallbackSnapshot && (
            <StatusPanel>
              <Clock3 size={16} />
              {t('feed.fallbackNotice')}
            </StatusPanel>
          )}
          {error && payload?.snapshot && (
            <StatusPanel>
              <BadgeAlert size={16} />
              {error}
            </StatusPanel>
          )}
          {isGenerating && payload?.snapshot && (
            <StatusPanel $generating>
              <LoaderCircle size={16} />
              {t('feed.generatingNewFeed')}
            </StatusPanel>
          )}

          <HeaderRow>
            <TitleBlock>
              <HeaderStamp>
                <SectionEyebrow>Daily Feed</SectionEyebrow>
                <HeaderDate>{headerDateLabel}</HeaderDate>
              </HeaderStamp>
            </TitleBlock>
            {canShowRegenerateButton && (
              <HeaderActions>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RefreshCcw size={14} />}
                  onClick={handleRefresh}
                  disabled={fetching}
                >
                  {fetching ? t('feed.requesting') : t('feed.regenerate')}
                </Button>
              </HeaderActions>
            )}
          </HeaderRow>

          <HeroGrid>
            {heroCards.map((card) => {
              const SourceIcon = getSourceIcon(card.source_type);
              const heroSurface = (
                <HeroCardSurface>
                  <HeroCardBadge>
                    <SourceIcon size={13} />
                    {t(buildSourceLabelKey(card.source_type))}
                  </HeroCardBadge>
                  <HeroCardTitle>{card.title}</HeroCardTitle>
                  <HeroCardSummary>{card.summary}</HeroCardSummary>
                  <HeroCardReason>{card.why_it_matters}</HeroCardReason>
                  <HeroCardFooter>
                    <span>{card.source_name}</span>
                    <span>{formatAbsoluteDate(card.published_at)}</span>
                  </HeroCardFooter>
                </HeroCardSurface>
              );
              return (
                <HeroCardWrap key={card.card_id}>
                  {externalLinksEnabled ? (
                    <HeroCardLink
                      href={card.source_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {heroSurface}
                    </HeroCardLink>
                  ) : (
                    <HeroCardLink
                      as="button"
                      type="button"
                      onClick={() => handleOpenHeroCard(card)}
                    >
                      {heroSurface}
                    </HeroCardLink>
                  )}
                  <HeroCardActionButton type="button" onClick={() => handleOpenHeroCard(card)}>
                    {t('feed.readMore')}
                  </HeroCardActionButton>
                </HeroCardWrap>
              );
            })}
          </HeroGrid>

          {lanes.map((lane, index) => (
            <LaneSection key={lane.lane_id}>
              <LaneHeader>
                <LaneHeadingBlock>
                  <LaneTitle>{lane.title}</LaneTitle>
                  <LaneDescription>
                    {lane.description || t('feed.laneDescription', { interest: getInterestLabel(lane.interest_key, customInterests) })}
                  </LaneDescription>
                </LaneHeadingBlock>
                <LaneMetaBadge>
                  <Sparkles size={14} />
                  {lane.items.length} picks
                </LaneMetaBadge>
              </LaneHeader>
              <LaneContentRow>
                {lane.lead_card && (
                  <FeedLaneLeadCard
                    lane={lane}
                    leadCard={lane.lead_card}
                    customInterests={customInterests}
                    onOpen={() => handleOpenLeadCard(lane)}
                  />
                )}
                <FeedLaneCarousel
                  lane={lane}
                  direction={index % 2 === 0 ? 'left' : 'right'}
                  onOpenCard={handleOpenLaneCard}
                />
              </LaneContentRow>
            </LaneSection>
          ))}
        </ContentArea>
      </Stage>
      <FeedDetailModal
        detail={selectedDetail}
        customInterests={customInterests}
        onClose={() => setSelectedDetail(null)}
      />
    </PageWrap>
  );
};

const pageReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
    filter: blur(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const lineClampCss = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PageWrap = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: auto;
  padding: 20px 24px 40px;
  animation: ${pageReveal} 0.45s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 12px 14px 32px;
  }
`;

const Stage = styled.div`
  width: 100%;
  max-width: ${FEED_PAGE_MAX_WIDTH}px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ContentArea = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const StatusPanel = styled.div<{ $tone?: 'warm'; $generating?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  border: ${({ theme, $tone, $generating }) =>
    $generating ? 'none' : `1px solid ${$tone === 'warm' ? `${theme.colors.accent}33` : theme.colors.borderLight}`};
  background: ${({ theme, $tone, $generating }) =>
    $generating ? 'transparent' : $tone === 'warm'
      ? theme.colors.accentLight
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(255,255,255,0.72)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
  animation: ${floatIn} 0.35s ease;

  svg {
    flex-shrink: 0;
  }

  ${({ $generating }) =>
    $generating &&
    css`
      & > svg {
        animation: ${spin} 1s linear infinite;
      }
    `}
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const HeaderStamp = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SectionEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.textPrimary : theme.colors.textMuted};
`;

const HeaderDate = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.textPrimary : theme.colors.textSecondary};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const HeroCardShell = styled.div`
  min-width: 0;
  min-height: 320px;
  display: flex;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(31, 31, 33, 0.72), rgba(21, 21, 23, 0.68))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(248,250,252,0.78))'};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
`;

const HeroCardWrap = styled.div`
  position: relative;
  min-width: 0;
  min-height: 320px;
`;

const HeroCardLink = styled.a`
  position: relative;
  min-height: 320px;
  display: flex;
  overflow: hidden;
  text-decoration: none;
  border-radius: 28px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(31, 31, 33, 0.72), rgba(21, 21, 23, 0.68))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(248,250,252,0.78))'};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transition: transform ${({ theme }) => theme.transitions.fast}, box-shadow ${({ theme }) => theme.transitions.fast};
  animation: ${floatIn} 0.4s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const HeroCardSurface = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
`;

const HeroCardBadge = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(255,255,255,0.74)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 700;
  backdrop-filter: blur(14px);
`;

const HeroCardTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.15rem, 2vw, 1.55rem);
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const HeroCardSummary = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  ${lineClampCss(4)}
`;

const HeroCardReason = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  ${lineClampCss(2)}
`;

const HeroCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const HeroCardActionButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(255,255,255,0.88)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 700;
  transition: transform ${({ theme }) => theme.transitions.fast}, border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.border};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 18px;
    right: 18px;
  }
`;

const LaneSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LaneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LaneHeadingBlock = styled.div`
  min-width: 0;
`;

const LaneTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LaneDescription = styled.p`
  margin: 6px 0 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LaneMetaBadge = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  border: ${({ theme }) => theme.mode === 'dark' ? '1px solid rgba(255,255,255,0.12)' : `1px solid ${theme.colors.borderLight}`};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(0, 0, 0, 0.52)'
    : 'rgba(255,255,255,0.8)'};
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: ${({ theme }) => theme.mode === 'dark' ? 'blur(10px)' : 'none'};
`;

const LaneContentRow = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, ${FEED_LANE_CARD_WIDTH}px) minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`;

const LaneLeadCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: ${FEED_LANE_CARD_HEIGHT}px;
  padding: 18px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(38, 38, 42, 0.72), rgba(24, 24, 27, 0.68))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,247,250,0.98))'};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  overflow: hidden;
`;

const LaneLeadEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 8px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accentLight};
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const LaneLeadBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
`;

const LaneLeadTitle = styled.h4`
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex-shrink: 0;
  ${lineClampCss(2)}
`;

const LaneLeadSnippet = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
  ${lineClampCss(3)}
`;

const LaneLeadPoints = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  flex-shrink: 0;
`;

const LaneLeadPoint = styled.li`
  font-size: 0.8125rem;
  line-height: 1.55;
  ${lineClampCss(1)}
`;

const LaneLeadMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LaneLeadAction = styled.button`
  width: 100%;
  flex-shrink: 0;
  padding: 11px 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'transparent'};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.08)'
    : theme.colors.accent};
  color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.textPrimary : '#fff'};
  font-size: 0.8125rem;
  font-weight: 700;
  backdrop-filter: ${({ theme }) => theme.mode === 'dark' ? 'blur(12px)' : 'none'};
  transition: transform ${({ theme }) => theme.transitions.fast}, opacity ${({ theme }) => theme.transitions.fast}, background ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.mode === 'dark'
      ? 'rgba(255,255,255,0.14)'
      : theme.colors.accent};
    opacity: ${({ theme }) => theme.mode === 'dark' ? 1 : 0.94};
  }
`;

const LaneCarouselWrap = styled.div`
  position: relative;
  min-width: 0;
`;

const LaneViewport = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  mask-image: linear-gradient(90deg, transparent, rgba(0,0,0,0.95) 6%, rgba(0,0,0,1) 100%);
  -webkit-mask-image: linear-gradient(90deg, transparent, rgba(0,0,0,0.95) 6%, rgba(0,0,0,1) 100%);
`;

const LaneTrack = styled.div`
  display: flex;
  gap: ${CARD_SCROLL_GAP}px;
  will-change: transform;
`;

const LaneArrowButton = styled.button<{ $side: 'right' }>`
  position: absolute;
  top: 50%;
  right: -18px;
  transform: translateY(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(30, 30, 34, 0.88)'
    : 'rgba(255, 255, 255, 0.92)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-50%) scale(1.08);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    background: ${({ theme }) => theme.mode === 'dark'
      ? 'rgba(50, 50, 56, 0.94)'
      : 'rgba(255, 255, 255, 1)'};
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }
`;

const LaneGroup = styled.div`
  display: flex;
  gap: ${CARD_SCROLL_GAP}px;
  flex-shrink: 0;
`;

const LaneCard = styled.article`
  width: min(${FEED_LANE_CARD_WIDTH}px, calc(100vw - 84px));
  min-width: min(${FEED_LANE_CARD_WIDTH}px, calc(100vw - 84px));
  height: ${FEED_LANE_CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(31, 31, 33, 0.72), rgba(21, 21, 23, 0.68))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))'};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transition: transform ${({ theme }) => theme.transitions.fast}, box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const LaneCardTopBar = styled.div`
  padding: 14px 14px 0;
  display: flex;
  align-items: flex-start;
`;

const LaneCardPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(0, 0, 0, 0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 700;
`;

const LaneCardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 10px;
`;

const LaneCardTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  line-height: 1.35;
  height: 2.7em;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  ${lineClampCss(2)}
`;

const LaneCardSummary = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-height: 3.1em;
  ${lineClampCss(3)}
`;

const LaneCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LaneCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
  padding: 0 16px 16px;
`;

const LaneCardReason = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-height: 2.3em;
  ${lineClampCss(2)}
`;

const LaneCardActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const LaneCardActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(255,255,255,0.88)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;
  font-weight: 700;
`;

const LaneCardActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
`;

const LaneEmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px;
  border-radius: 22px;
  border: 1px dashed ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(30, 30, 34, 0.52)'
    : 'rgba(255,255,255,0.72)'};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  text-align: center;
`;

const LaneEmptyIcon = styled.div`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.accentLight};
`;

const LaneEmptyTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LaneEmptyDesc = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LaneSkeletonCard = styled.div`
  width: min(${FEED_LANE_CARD_WIDTH}px, calc(100vw - 84px));
  min-width: min(${FEED_LANE_CARD_WIDTH}px, calc(100vw - 84px));
  height: ${FEED_LANE_CARD_HEIGHT}px;
  padding: 16px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(31,31,33,0.9)'
    : 'rgba(255,255,255,0.92)'};
`;

const DetailModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const LeadDetailInner = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LeadDetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LeadDetailHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const LeadDetailKicker = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const BriefCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(255,255,255,0.88)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: transform ${({ theme }) => theme.transitions.fast}, border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

const LeadDetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LeadDetailMetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(255,255,255,0.82)'};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LeadDetailTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.6rem, 2.8vw, 2.3rem);
  line-height: 1.16;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DetailModalSubtitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const DetailModalLeadSnippet = styled.p`
  margin: 0;
  padding: 16px 18px;
  border-radius: 18px;
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(248,250,252,0.9)'};
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LeadDetailHighlights = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.03)'
    : 'rgba(255,255,255,0.82)'};
`;

const DetailSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailSectionTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DetailBodyText = styled.div`
  margin: 0;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LeadDetailArticle = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LeadDetailParagraph = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.85;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LeadDetailParagraphLead = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DetailBulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailSourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailSourceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.03)'
    : 'rgba(255,255,255,0.82)'};
`;

const DetailSourceLabel = styled.span`
  display: inline-flex;
  align-self: flex-start;
  padding: 6px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accentLight};
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.72rem;
  font-weight: 700;
`;

const DetailSourceAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
`;

const DetailSourceMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DetailCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.03)'
    : 'rgba(255,255,255,0.82)'};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailOriginalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.025)'
    : 'rgba(248,250,252,0.9)'};
`;

const DetailOriginalTitleLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DetailOriginalTitleText = styled.div`
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const DetailActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
